import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** SPA 内切到其他视图再切回时恢复本页纵向滚动（组件卸载时会写入） */
let startLandingPersistedScrollTop = 0;
import FJScroller from '../../common/components/FJScroller/FJScroller';
import styles from './FJAIStartLanding.module.less';
import FJLinkIcon from '../../assets/link.svg?react';
import FJArrowWhiteIcon from '../../assets/arrow-white.svg?react';
import FJUploadIcon from '../../assets/upload.svg?react';
import FJGoogleDriveIcon from '../../assets/google-drive.svg?react';
import FJDragFileIcon from '../../assets/drag-file.svg?react';
import FJButton, { FJButtonType } from '../../common/components/FJButton/FJButton';
import FJLocalStore from '../../store/FJLocalStore';
import FJRequirementsUtil from '../../util/FJRequirementsUtil';
import { setLtsUploadSource, LTS_SOURCE } from '../../util/FJLongToShortEa';
import FJConfig from '@src/config/FJConfig';

function pickFirstVideoFile(files: FileList | null): File | null {
    if (!files?.length) return null;
    for (let i = 0; i < files.length; i++) {
        const f = files[i];
        if (f.type.startsWith('video/')) return f;
    }
    return null;
}

interface FJAIStartLandingProps {
    /** 与 Redux `startLanding.fileUrl` 受控绑定 */
    fileUrl: string;
    onFileUrlChange: (value: string) => void;
    /** 点击箭头或回车时，当前输入框中的链接 */
    onSubmitUrl?: (url: string) => void;
    /** 用户选择本地视频文件 */
    onBrowseFile?: (file: File) => void;
    /** 开始上传（浏览/拖拽/链接等）时退出最近文件多选 */
    onExitMultiSelect?: () => void;
    /** 本地浏览文件解析中，来自 Redux `startLanding.bFLoading` */
    bFLoading: boolean;
    /** 点击 Google Drive（可由接入方实现 OAuth 等） */
    onGoogleDrive?: (callback?: () => void) => void;
    /** 有数据时替换底部 illustration 的区域（由容器注入） */
    belowFold?: ReactNode;
    isVerifyingToGoogleDrive: boolean;
}

export default function FJAIStartLanding({
    fileUrl,
    onFileUrlChange,
    onSubmitUrl,
    onBrowseFile,
    onExitMultiSelect,
    onGoogleDrive,
    belowFold,
    isVerifyingToGoogleDrive,
    bFLoading,
}: FJAIStartLandingProps) {
    const input = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [fileDragOver, setFileDragOver] = useState(false);

    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTop = startLandingPersistedScrollTop;
        return () => {
            startLandingPersistedScrollTop = el.scrollTop;
        };
    }, []);

    const submit = useCallback(
        (value?: string) => {
            const v = value || fileUrl.trim();
            if (v) {
                onExitMultiSelect?.();
                onSubmitUrl?.(v);
            }
        },
        [fileUrl, onSubmitUrl, onExitMultiSelect],
    );

    const onFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (f) {
                onExitMultiSelect?.();
                setLtsUploadSource(LTS_SOURCE.LOCAL_FILE);
                onBrowseFile?.(f);
            }
            e.target.value = '';
        },
        [onBrowseFile, onExitMultiSelect],
    );

    const clearFileDragOver = useCallback(() => {
        setFileDragOver(false);
    }, []);

    useEffect(() => {
        if (!onBrowseFile || bFLoading) {
            queueMicrotask(() => setFileDragOver(false));
            return;
        }
        const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');

        const onWindowDragEnter = (e: DragEvent) => {
            if (!hasFiles(e)) return;
            setFileDragOver(true);
        };
        const onWindowDragEnd = () => {
            setFileDragOver(false);
        };

        window.addEventListener('dragenter', onWindowDragEnter, true);
        window.addEventListener('dragend', onWindowDragEnd, true);
        return () => {
            window.removeEventListener('dragenter', onWindowDragEnter, true);
            window.removeEventListener('dragend', onWindowDragEnd, true);
        };
    }, [onBrowseFile, bFLoading]);

    const handleOverlayDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (bFLoading || !onBrowseFile) return;
            if (!e.dataTransfer.types?.includes('Files')) return;
            e.dataTransfer.dropEffect = 'copy';
        },
        [bFLoading, onBrowseFile],
    );

    const handleOverlayDragLeave = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const next = e.relatedTarget as Node | null;
            if (next && (e.currentTarget as HTMLElement).contains(next)) return;
            clearFileDragOver();
        },
        [clearFileDragOver],
    );

    const handleOverlayDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            clearFileDragOver();
            if (bFLoading || !onBrowseFile) return;
            const file = pickFirstVideoFile(e.dataTransfer.files);
            if (file) {
                onExitMultiSelect?.();
                setLtsUploadSource(LTS_SOURCE.LOCAL_FILE);
                onBrowseFile(file);
            }
        },
        [bFLoading, onBrowseFile, onExitMultiSelect, clearFileDragOver],
    );

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            submit();
        }
    };

    const _onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        // onPaste 事件在 input 的 value 更新前触发，所以需要从 clipboardData 获取数据
        const pastedText = e.clipboardData.getData('text/plain');
        if (pastedText) {
            // 我们不阻止默认行为，让 React 的受控组件流程自然更新 input 的值
            // 同时，我们可以立即用粘贴的内容触发提交
            submit(pastedText);
        }
    };

    return (
        <FJScroller ref={scrollerRef} className={classNames(styles.box, !belowFold && styles.box_below_fold)}>
            <div className={styles.hero}>
                <h1 className={styles.title}>{FJLocalStore._('long-to-shorts')}</h1>
                <p className={styles.subtitle}>{FJLocalStore._('turn-your-long-videos-into-viral-short-videos')}</p>

                <div className={styles.input_row}>
                    <span className={styles.link_icon} aria-hidden>
                        <FJLinkIcon />
                    </span>
                    <input
                        ref={input}
                        className={styles.url_input}
                        type='url'
                        value={fileUrl}
                        onChange={e => onFileUrlChange(e.target.value)}
                        placeholder={FJLocalStore._('paste-a-video-link-youtube-etc')}
                        onKeyDown={onKeyDown}
                        onPaste={_onPaste}
                        aria-label={FJLocalStore._('paste-a-video-link-youtube-etc')}
                    />
                    <button type='button' className={styles.submit_btn} onClick={() => submit()}>
                        <FJArrowWhiteIcon />
                    </button>
                </div>

                <div className={styles.secondary_row}>
                    <input
                        ref={fileRef}
                        className={styles.hidden_file}
                        type='file'
                        accept='video/*'
                        onChange={onFileChange}
                    />
                    <FJButton
                        className={styles.upload_btn}
                        textClassName={styles.upload_btn_text}
                        type={FJButtonType.WHITE}
                        icon={<FJUploadIcon />}
                        text='browse-files'
                        isLoading={bFLoading}
                        onClick={() => {
                            onExitMultiSelect?.();
                            FJRequirementsUtil.eaLog('browse_file_click');
                            fileRef.current?.click();
                        }}
                    />
                    <FJButton
                        className={styles.upload_btn}
                        textClassName={styles.upload_btn_text}
                        type={FJButtonType.WHITE}
                        icon={<FJGoogleDriveIcon />}
                        text='google-drive'
                        onClick={() => onGoogleDrive?.()}
                        isLoading={isVerifyingToGoogleDrive}
                    />
                </div>
            </div>

            {belowFold ? (
                <div className={styles.below_fold}>{belowFold}</div>
            ) : (
                <div className={styles.illustration}>
                    <video
                        src={FJConfig.VIDEO_PATH + 'long-to-shorts.mp4'}
                        className={styles.illustration_img}
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                </div>
            )}

            {onBrowseFile &&
                fileDragOver &&
                !bFLoading &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={styles.drop_overlay}
                        onDragOver={handleOverlayDragOver}
                        onDragLeave={handleOverlayDragLeave}
                        onDrop={handleOverlayDrop}
                        role='presentation'
                    >
                        <div className={styles.drop_overlay_inner}>
                            <FJDragFileIcon className={styles.drop_svg} aria-hidden />
                            <span className={styles.drop_overlay_label}>{FJLocalStore._('drag-add-files')}</span>
                        </div>
                    </div>,
                    document.body,
                )}
        </FJScroller>
    );
}
